import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  FiChevronDown,
  FiMenu,
  FiX,
  FiArrowRight,
  FiCpu,
  FiZap,
  FiMessageSquare,
  FiShoppingBag,
  FiHeart,
  FiTruck,
  FiBookOpen,
  FiBriefcase,
  FiLogIn,
  FiCalendar,
} from "react-icons/fi";

import "../Header.css";
import logo from "../../assets/dropgear-favicon.png";


const Header = () => {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const headerRef = useRef(null);
  const location = useLocation();

  /* =========================================================
     CLOSE MENUS WHEN ROUTE CHANGES
  ========================================================= */
  useEffect(() => {
    setMobileMenu(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  /* =========================================================
     CLOSE DROPDOWN WHEN CLICKING OUTSIDE
  ========================================================= */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        headerRef.current &&
        !headerRef.current.contains(event.target)
      ) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  /* =========================================================
     PREVENT BODY SCROLL WHEN MOBILE MENU OPEN
  ========================================================= */
  useEffect(() => {
    if (mobileMenu) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenu]);

  /* =========================================================
     TOGGLE DROPDOWN
  ========================================================= */
  const toggleDropdown = (menu) => {
    setActiveDropdown((prev) =>
      prev === menu ? null : menu
    );
  };

  /* =========================================================
     CLOSE ALL MENUS
  ========================================================= */
  const closeAllMenus = () => {
    setMobileMenu(false);
    setActiveDropdown(null);
  };

  /* =========================================================
     SOLUTIONS
  ========================================================= */
  const solutions = [
    {
      title: "AI Solutions",
      description:
        "Intelligent AI systems designed to solve real business problems.",
      icon: <FiCpu />,
      path: "/ai-solutions",
    },
    {
      title: "AI Automation",
      description:
        "Automate workflows, operations, and repetitive business processes.",
      icon: <FiZap />,
      path: "/ai-automation",
    },
    {
      title: "AI Agents",
      description:
        "Autonomous AI agents that understand, reason, and take action.",
      icon: <FiMessageSquare />,
      path: "/ai-agents",
    },
  ];

  /* =========================================================
     INDUSTRIES
  ========================================================= */
  const industries = [
    {
      title: "Retail & E-commerce",
      description:
        "Smarter customer and commerce experiences.",
      icon: <FiShoppingBag />,
      path: "/retail",
    },
    {
      title: "Healthcare",
      description:
        "AI-powered healthcare intelligence and automation.",
      icon: <FiHeart />,
      path: "/healthcare",
    },
    {
      title: "Logistics",
      description:
        "Intelligent operations and supply-chain systems.",
      icon: <FiTruck />,
      path: "/logistics",
    },
    {
      title: "Finance",
      description:
        "AI systems for financial intelligence and operations.",
      icon: <FiBriefcase />,
      path: "/finance",
    },
    {
      title: "Professional Services",
      description:
        "AI-powered productivity and business workflows.",
      icon: <FiBookOpen />,
      path: "/professional-services",
    },
    {
      title: "Enterprise",
      description:
        "Scalable AI infrastructure for modern enterprises.",
      icon: <FiCpu />,
      path: "/enterprise",
    },
  ];

  return (
    <>
      <header
        className={`site-header ${
          mobileMenu ? "mobile-menu-active" : ""
        }`}
        ref={headerRef}
      >
        <div className="header-container">

          {/* =====================================================
              LOGO
          ====================================================== */}
          <div className="header-logo">
            <a
              href="/"
              className="logo-link"
              onClick={closeAllMenus}
            >
              <div className="logo-symbol">
                <img src={logo} alt="dropgear" />
              </div>

              <div className="logo-text">
                <span className="logo-main">
                  Dropgear<span> IO</span>
                </span>

                {/* <small>PRIVATE LIMITED</small> */}
              </div>
            </a>
          </div>


          {/* =====================================================
              DESKTOP NAVIGATION
          ====================================================== */}
          <nav className="desktop-navigation">

            {/* SOLUTIONS */}
            <div
              className={`nav-dropdown ${
                activeDropdown === "solutions"
                  ? "dropdown-active"
                  : ""
              }`}
            >
              <button
                className="nav-dropdown-button"
                onClick={() => toggleDropdown("solutions")}
                aria-expanded={
                  activeDropdown === "solutions"
                }
              >
                Solutions
                <FiChevronDown />
              </button>

              {activeDropdown === "solutions" && (
                <div className="mega-dropdown solutions-dropdown">

                  <div className="mega-dropdown-header">
                    <div>
                      <span className="dropdown-eyebrow">
                        WHAT WE BUILD
                      </span>

                      <h3>
                        Intelligent solutions
                        <br />
                        for modern business.
                      </h3>
                    </div>

                    {/* <Link
                      to="/solutions"
                      className="view-all-link"
                      onClick={closeAllMenus}
                    >
                      View all solutions
                      <FiArrowRight />
                    </Link> */}
                  </div>

                  <div className="dropdown-items solutions-items">

                    {solutions.map((solution) => (
                      <a
                        href={solution.path}
                        className="dropdown-item"
                        key={solution.title}
                        onClick={closeAllMenus}
                      >
                        <div className="dropdown-icon">
                          {solution.icon}
                        </div>

                        <div className="dropdown-content">
                          <h4>{solution.title}</h4>
                          <p>{solution.description}</p>
                        </div>

                        <FiArrowRight className="item-arrow" />
                      </a>
                    ))}

                  </div>

                  <div className="dropdown-bottom">
                    <span>
                      AI-native systems built for real-world use.
                    </span>

                    <a
                      href="/contact"
                      onClick={closeAllMenus}
                    >
                      Talk to our team
                      <FiArrowRight />
                    </a>
                  </div>

                </div>
              )}
            </div>


            {/* INDUSTRIES */}
            <div
              className={`nav-dropdown ${
                activeDropdown === "industries"
                  ? "dropdown-active"
                  : ""
              }`}
            >
              <button
                className="nav-dropdown-button"
                onClick={() => toggleDropdown("industries")}
                aria-expanded={
                  activeDropdown === "industries"
                }
              >
                Industries
                <FiChevronDown />
              </button>

              {activeDropdown === "industries" && (
                <div className="mega-dropdown industries-dropdown">

                  <div className="mega-dropdown-header">
                    <div>
                      <span className="dropdown-eyebrow">
                        INDUSTRIES
                      </span>

                      <h3>
                        Intelligence across
                        <br />
                        different industries.
                      </h3>
                    </div>

                    {/* <Link
                      to="/industries"
                      className="view-all-link"
                      onClick={closeAllMenus}
                    >
                      Explore industries
                      <FiArrowRight />
                    </Link> */}
                  </div>

                  <div className="dropdown-items industries-items">

                    {industries.map((industry) => (
                      <a
                        href={industry.path}
                        className="dropdown-item"
                        key={industry.title}
                        onClick={closeAllMenus}
                      >
                        <div className="dropdown-icon">
                          {industry.icon}
                        </div>

                        <div className="dropdown-content">
                          <h4>{industry.title}</h4>
                          <p>{industry.description}</p>
                        </div>

                        <FiArrowRight className="item-arrow" />
                      </a>
                    ))}

                  </div>

                </div>
              )}
            </div>


            {/* HOW IT WORKS */}
            <a
              href="/how-it-works"
              className="nav-link"
              onClick={closeAllMenus}
            >
              How It Works
            </a>


            {/* BLOG */}
            {/* <Link
              to="/blog"
              className="nav-link"
              onClick={closeAllMenus}
            >
              Blog
            </Link> */}


            {/* CONTACT */}
            <a
              href="/contact"
              className="nav-link"
              onClick={closeAllMenus}
            >
              Contact
            </a>

          </nav>


          {/* =====================================================
              DESKTOP ACTIONS
          ====================================================== */}
          <div className="header-actions">

            <a
              href="/login"
              className="login-button"
              onClick={closeAllMenus}
            >
              <FiLogIn />
              <span>Login</span>
            </a>

            <a
              href="/book-demo"
              className="demo-button"
              onClick={closeAllMenus}
            >
              <span>Book Demo</span>
              <FiCalendar />
            </a>

          </div>


          {/* =====================================================
              MOBILE MENU BUTTON
          ====================================================== */}
          <button
            className="mobile-menu-button"
            onClick={() => setMobileMenu(!mobileMenu)}
            aria-label="Toggle navigation"
            aria-expanded={mobileMenu}
          >
            {mobileMenu ? <FiX /> : <FiMenu />}
          </button>

        </div>


        {/* =====================================================
            MOBILE NAVIGATION
        ====================================================== */}
        <div
          className={`mobile-navigation ${
            mobileMenu
              ? "mobile-navigation-open"
              : ""
          }`}
        >
          <div className="mobile-navigation-inner">

            {/* MOBILE SOLUTIONS */}
            <div
              className={`mobile-dropdown ${
                activeDropdown === "solutions-mobile"
                  ? "mobile-dropdown-active"
                  : ""
              }`}
            >
              <button
                className="mobile-dropdown-button"
                onClick={() =>
                  toggleDropdown("solutions-mobile")
                }
              >
                <span>Solutions</span>
                <FiChevronDown />
              </button>

              <div className="mobile-dropdown-content">

                {solutions.map((solution) => (
                  <a
                    href={solution.path}
                    className="mobile-dropdown-item"
                    key={solution.title}
                    onClick={closeAllMenus}
                  >
                    <div className="mobile-item-icon">
                      {solution.icon}
                    </div>

                    <div>
                      <strong>
                        {solution.title}
                      </strong>

                      <span>
                        {solution.description}
                      </span>
                    </div>

                    <FiArrowRight />
                  </a>
                ))}

                {/* <Link
                  to="/solutions"
                  className="mobile-view-all"
                  onClick={closeAllMenus}
                >
                  View all solutions
                  <FiArrowRight />
                </Link> */}

              </div>
            </div>


            {/* MOBILE INDUSTRIES */}
            <div
              className={`mobile-dropdown ${
                activeDropdown === "industries-mobile"
                  ? "mobile-dropdown-active"
                  : ""
              }`}
            >
              <button
                className="mobile-dropdown-button"
                onClick={() =>
                  toggleDropdown("industries-mobile")
                }
              >
                <span>Industries</span>
                <FiChevronDown />
              </button>

              <div className="mobile-dropdown-content">

                {industries.map((industry) => (
                  <a
                    href={industry.path}
                    className="mobile-dropdown-item"
                    key={industry.title}
                    onClick={closeAllMenus}
                  >
                    <div className="mobile-item-icon">
                      {industry.icon}
                    </div>

                    <div>
                      <strong>
                        {industry.title}
                      </strong>

                      <span>
                        {industry.description}
                      </span>
                    </div>

                    <FiArrowRight />
                  </a>
                ))}

                {/* <Link
                  to="/industries"
                  className="mobile-view-all"
                  onClick={closeAllMenus}
                >
                  Explore all industries
                  <FiArrowRight />
                </Link> */}

              </div>
            </div>


            {/* MOBILE NORMAL LINKS */}

            <a
              href="/how-it-works"
              className="mobile-nav-link"
              onClick={closeAllMenus}
            >
              How It Works
            </a>

            {/* <Link
              to="/blog"
              className="mobile-nav-link"
              onClick={closeAllMenus}
            >
              Blog
            </Link> */}

            <a
              href="/contact"
              className="mobile-nav-link"
              onClick={closeAllMenus}
            >
              Contact
            </a>


            {/* MOBILE ACTIONS */}
            <div className="mobile-actions">

              <a
                href="/login"
                className="mobile-login-button"
                onClick={closeAllMenus}
              >
                <FiLogIn />
                Login
              </a>

              <a
                href="/book-demo"
                className="mobile-demo-button"
                onClick={closeAllMenus}
              >
                Book a Demo
                <FiArrowRight />
              </a>

            </div>

          </div>
        </div>

      </header>


      {/* =====================================================
          MOBILE OVERLAY
      ====================================================== */}
      {mobileMenu && (
        <div
          className="mobile-menu-overlay"
          onClick={closeAllMenus}
        />
      )}
    </>
  );
};

export default Header;